import React, { useMemo } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useBodyClass } from '../hooks/useBodyClass';

const BODY_CLASSES = ['hold-transition', 'sidebar-mini', 'sidebar-expand-lg', 'layout-fixed', 'sidebar-collapse'];

type AdminLayoutProps = {
  title: string;
  subtitle?: string;
  headerActions?: React.ReactNode;
  children: React.ReactNode;
  onLogout?: () => void;
};

export function AdminLayout({ title, subtitle, headerActions, children, onLogout }: AdminLayoutProps) {
  const { user, logout } = useAuth();
  const location = useLocation();

  useBodyClass(BODY_CLASSES);

  const menuItems = useMemo(
    () => [
      { to: '/', label: 'Practice workspace', icon: 'fas fa-chart-line' },
      { to: '/settings/users', label: 'System Settings', icon: 'fas fa-cogs', adminOnly: true },
    ],
    []
  );

  const activePath = location.pathname.startsWith('/settings') ? '/settings/users' : location.pathname;

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      logout();
    }
  };

  return (
    <div className="app-wrapper">
      <nav className="app-header navbar navbar-expand bg-body border-bottom shadow-sm">
        <div className="container-fluid">
          <ul className="navbar-nav">
            <li className="nav-item">
              <button
                type="button"
                className="nav-link btn btn-link text-secondary"
                data-lte-toggle="sidebar"
                aria-label="Toggle sidebar"
              >
                <i className="fas fa-bars" aria-hidden="true" />
              </button>
            </li>
          </ul>
          <ul className="navbar-nav ms-auto align-items-center">
            {user && (
              <li className="nav-item me-3 text-muted small">
                <span className="d-block fw-semibold text-dark">{user.name || user.email}</span>
                <span className="d-block text-muted">{user.role === 'admin' ? 'Administrator' : 'Member'}</span>
              </li>
            )}
            <li className="nav-item">
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleLogout}>
                <i className="fas fa-sign-out-alt me-1" aria-hidden="true" /> Sign out
              </button>
            </li>
          </ul>
        </div>
      </nav>

      <aside className="app-sidebar bg-body-secondary shadow" data-bs-theme="dark">
        <div className="sidebar-brand">
          <Link to="/" className="brand-link">
            <span className="brand-text fw-semibold text-uppercase">Selective Tests</span>
          </Link>
        </div>
        <div className="sidebar-wrapper">
          <nav className="mt-3">
            <ul className="nav sidebar-menu flex-column" role="menu">
              {menuItems
                .filter((item) => !item.adminOnly || user?.role === 'admin')
                .map((item) => (
                  <li key={item.to} className="nav-item">
                    <NavLink
                      to={item.to}
                      className={({ isActive }) =>
                        `nav-link ${isActive || activePath === item.to ? 'active' : ''}`
                      }
                    >
                      <i className={`${item.icon} nav-icon`} aria-hidden="true" />
                      <p>{item.label}</p>
                    </NavLink>
                  </li>
                ))}
            </ul>
          </nav>
        </div>
      </aside>

      <main className="app-main">
        <div className="app-content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-sm-8">
                <h1 className="m-0 fw-semibold">{title}</h1>
                {subtitle ? <p className="text-muted mb-0">{subtitle}</p> : null}
              </div>
              {headerActions ? <div className="col-sm-4 text-sm-end mt-3 mt-sm-0">{headerActions}</div> : null}
            </div>
          </div>
        </div>
        <div className="app-content">
          <div className="container-fluid">{children}</div>
        </div>
      </main>
    </div>
  );
}
